import React from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'antd';
import { Chat, ChatSlash } from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import { formatRole } from '../helpers';
import {
  displayableTranscriptItems,
  hasTranscriptContent,
  parseTranscriptItems,
} from '../transcriptContent';
import './callTranscript.scss';

const { Panel } = Collapse;

const transcriptItemKey = (item, previousItem, nextItem) => [
  item?.role,
  item?.content,
  previousItem?.role,
  previousItem?.content,
  nextItem?.role,
  nextItem?.content,
].filter(Boolean).join('|');

const CallTranscript = ({ transcript, userName, defaultExpanded }) => {
  if (!hasTranscriptContent(transcript)) {
    return (
      <div className="call-transcript-section">
        <Typography variant="text" size="small" color={COLORS.TEXT_SECONDARY_LITE} bold>
          <ChatSlash size={18} weight="bold" />
          {' '}
          No transcript available.
        </Typography>
      </div>
    );
  }

  const parseTranscript = (transcriptValue) => {
    const parsed = parseTranscriptItems(transcriptValue);

    // null (not []) means the value would not parse or is not an array -- see
    // parseTranscriptItems. hasTranscriptContent deliberately treats that as
    // content so this message is reachable rather than hidden as "empty".
    if (parsed === null) {
      return (
        <Typography variant="text" size="small" color={COLORS.ERROR_LITE}>
          Error parsing transcript
        </Typography>
      );
    }

    const transcriptArray = displayableTranscriptItems(parsed);
    return transcriptArray.map((item, index) => {
      const isUser = item?.role === 'user';
      const previousItem = transcriptArray[index - 1];
      const nextItem = transcriptArray[index + 1];
      const shouldShowRole = previousItem?.role !== item?.role;
      const speakerName = formatRole(item?.role) === 'User' ? userName : 'MAIRA™';

      return (
        <div className={`call-transcript-item${isUser ? ' user' : ''}`} key={transcriptItemKey(item, previousItem, nextItem)}>
          {shouldShowRole && (
            <div className="call-transcript-item-role">
              <Typography variant="text" size="small" bold>
                {speakerName}
              </Typography>
            </div>
          )}
          <div className={`call-transcript-item-content${isUser ? ' user' : ''}`}>
            <Typography variant="text" size="medium">
              {item?.content}
            </Typography>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="call-transcript-section">
      <Collapse ghost defaultActiveKey={defaultExpanded ? ['1'] : []}>
        <Panel
          header={(
            <Typography variant="text" size="small" bold>
              <Chat size={18} weight="bold" />
              {' '}
              Call Transcript
            </Typography>
          )}
          key="1"
        >
          <div className="call-transcript-items">
            {parseTranscript(transcript)}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

CallTranscript.defaultProps = {
  transcript: null,
  userName: 'User',
  defaultExpanded: true,
};

CallTranscript.propTypes = {
  // Either the raw JSON-column value (already parsed to an array) or a
  // stringified transcript some caller re-encoded -- parseTranscript and
  // hasTranscriptContent both handle either shape.
  transcript: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  userName: PropTypes.string,
  defaultExpanded: PropTypes.bool,
};

// Default export only, deliberately: the transcript-content helpers live in
// ../transcriptContent so importers do not have to pull in this component (and
// so the slide-out tests that mock this module with a default-only stub cannot
// break an unrelated importer depending on test file order).
export default CallTranscript;
